/**
 * Heuristic user onboarding / offboarding detection for UI (Custom Workflows banner fallback).
 *
 * Primary classification for chat is the AI Overview LLM field `user_lifecycle`. Use this module
 * only when that call fails or omits the field — not when the LLM explicitly returns `none`.
 */

export type UserLifecycle = "none" | "onboarding" | "offboarding";

/** Normalize PSA / analytics labels for matching (trim, lowercase, collapse spaces). */
export function normalizeTicketLifecycleLabel(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** True when a single field suggests offboarding (aligned with automatable dashboard + word-boundary offboard). */
export function labelIndicatesOffboarding(label: string): boolean {
  const n = normalizeTicketLifecycleLabel(label);
  if (!n) return false;
  if (n.includes("offboarding")) return true;
  if (/\boffboard(s|ing|ed)?\b/i.test(n)) return true;
  return false;
}

/** User-scoped onboarding: avoid generic "onboarding" without clear provisional hire/user context. */
export function labelIndicatesUserOnboarding(label: string): boolean {
  const n = normalizeTicketLifecycleLabel(label);
  if (!n) return false;
  // Docs/packets/forms that mention onboarding are not themselves user provisioning.
  if (
    /\bonboarding\b/.test(n) &&
    /\b(document|documents|doc|form|forms|guide|checklist|packet|template|folder|sharepoint|onedrive|file|files|link|links)\b/.test(
      n
    )
  ) {
    return false;
  }
  if (!n.includes("onboarding")) return false;
  if (n.includes("user")) return true;
  if (/\bnew\s+hire\b/.test(n) || /\bemployee\b/.test(n) || /\bprovision(ing)?\b/.test(n)) return true;
  return false;
}

/**
 * Combine multiple label strings; offboarding wins over onboarding if both appear in the blob.
 */
export function lifecycleFromLabels(...labels: (string | null | undefined)[]): UserLifecycle {
  for (const raw of labels) {
    const s = String(raw || "").trim();
    if (s && labelIndicatesOffboarding(s)) return "offboarding";
  }
  for (const raw of labels) {
    const s = String(raw || "").trim();
    if (s && labelIndicatesUserOnboarding(s)) return "onboarding";
  }
  return "none";
}

function pickStr(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t || null;
  }
  return null;
}

const META_KEYS = [
  "ticketType",
  "ticket_type",
  "category",
  "ticketCategory",
  "ticket_category",
] as const;

/**
 * Fallback lifecycle from a supervisor/API ticket object: PSA type & category first, then subject + description.
 */
export function userLifecycleFromTicketRecordHeuristic(
  t: Record<string, unknown> | null | undefined
): UserLifecycle {
  if (!t || typeof t !== "object") return "none";

  const meta: string[] = [];
  for (const k of META_KEYS) {
    const s = pickStr(t[k]);
    if (s) meta.push(s);
  }
  let life = lifecycleFromLabels(...meta);
  if (life !== "none") return life;

  const subj = pickStr(t.subject) || pickStr(t.title);
  const desc = pickStr(t.description);
  const blob = [subj, desc].filter(Boolean).join("\n");
  if (blob) {
    life = lifecycleFromLabels(blob);
  }
  return life;
}

/** Analytics row: LLM ticket_type + source ticket_type. */
export function userLifecycleFromAutomatableRecord(record: {
  analysis?: { ticket_type?: string | null };
  ticket?: { ticket_type?: string | null };
} | null): UserLifecycle {
  return lifecycleFromLabels(
    record?.analysis?.ticket_type ?? "",
    record?.ticket?.ticket_type ?? ""
  );
}
